import React, { FormEvent, useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Form,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Spinner,
} from "reactstrap";
import { requestJson } from "../../../../helpers/api";

export type ConciliarPlataformaResponse = {
  ok?: boolean;
  message?: string;
  resumen?: string[];
  detalles?: string[];
  registrosProcesados?: number;
};

type ConciliarPlataformaPanelProps = {
  endpoint: string;
  titulo: string;
  descripcion?: string;
  archivoLabel?: string;
  archivoAyuda?: string;
  aceptados?: string;
  periodoLabel?: string;
  periodoPlaceholder?: string;
  observacionesLabel?: string;
  observacionesPlaceholder?: string;
  botonTexto?: string;
  resumenTitulo?: string;
  limpiarTexto?: string;
  layout?: 'card' | 'plain';
  onSuccess?: (payload: ConciliarPlataformaResponse) => void;
};

const ConciliarPlataformaPanel = ({
  endpoint,
  titulo,
  descripcion = "Procesá la planilla exportada para contrastarla contra las cajas declaradas y registrar diferencias.",
  archivoLabel = "Archivo de referencia",
  archivoAyuda = "Se admiten archivos XLSX, XLS o CSV exportados desde la plataforma.",
  aceptados = ".xlsx,.xls,.csv",
  periodoLabel = "Periodo de referencia (opcional)",
  periodoPlaceholder = "Ej. Enero 2026",
  observacionesLabel = "Observaciones (opcional)",
  observacionesPlaceholder = "Comentarios o notas adicionales para Tesorería.",
  botonTexto = "Procesar planilla",
  resumenTitulo = "Resumen del procesamiento",
  limpiarTexto = "Limpiar",
  layout = "card",
  onSuccess,
}: ConciliarPlataformaPanelProps) => {
  const inputArchivoRef = useRef<HTMLInputElement | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [periodo, setPeriodo] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [mensajeError, setMensajeError] = useState<string | null>(null);
  const [mensajeExito, setMensajeExito] = useState<string | null>(null);
  const [detalles, setDetalles] = useState<string[]>([]);

  const puedeEnviar = useMemo(() => !!archivo && !procesando, [archivo, procesando]);
  const puedeLimpiar = useMemo(
    () =>
      !!archivo ||
      periodo.trim().length > 0 ||
      observaciones.trim().length > 0 ||
      detalles.length > 0 ||
      !!mensajeError ||
      !!mensajeExito,
    [archivo, periodo, observaciones, detalles, mensajeError, mensajeExito],
  );

  const limpiarFormulario = useCallback(() => {
    setArchivo(null);
    setPeriodo("");
    setObservaciones("");
    setDetalles([]);
    setMensajeError(null);
    setMensajeExito(null);
    if (inputArchivoRef.current) {
      inputArchivoRef.current.value = "";
    }
  }, []);

  const manejarCambioArchivo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const seleccionado = event.target.files?.[0] ?? null;
    setArchivo(seleccionado);
    setMensajeError(null);
    setMensajeExito(null);
    setDetalles([]);
  };

  const manejarProcesamiento = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!archivo) {
      setMensajeError("Seleccioná un archivo para continuar.");
      return;
    }
    setProcesando(true);
    setMensajeError(null);
    setMensajeExito(null);
    setDetalles([]);
    try {
      const formData = new FormData();
      formData.append("archivo", archivo);
      if (periodo.trim()) {
        formData.append("periodo", periodo.trim());
      }
      if (observaciones.trim()) {
        formData.append("observaciones", observaciones.trim());
      }
      const respuesta = await requestJson<ConciliarPlataformaResponse>(endpoint, {
        method: "POST",
        body: formData,
      });
      const resumen: string[] = [];
      if (typeof respuesta.registrosProcesados === "number") {
        resumen.push(`Registros procesados: ${respuesta.registrosProcesados}`);
      }
      if (Array.isArray(respuesta.resumen)) {
        respuesta.resumen
          .filter((linea): linea is string => typeof linea === "string" && linea.trim().length > 0)
          .forEach((linea) => resumen.push(linea));
      }
      if (Array.isArray(respuesta.detalles)) {
        respuesta.detalles
          .filter((linea): linea is string => typeof linea === "string" && linea.trim().length > 0)
          .forEach((linea) => resumen.push(linea));
      }
      setDetalles(resumen);
      setMensajeExito(respuesta.message ?? "El archivo se procesó correctamente.");
      onSuccess?.(respuesta);
      setArchivo(null);
      setPeriodo("");
      setObservaciones("");
      if (inputArchivoRef.current) {
        inputArchivoRef.current.value = "";
      }
    } catch (error) {
      const mensaje =
        error instanceof Error ? error.message : "No se pudo completar el procesamiento. Intentá nuevamente.";
      setMensajeError(mensaje);
    } finally {
      setProcesando(false);
    }
  };

  const baseId = useMemo(() => {
    const normalizado = titulo.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-_]/g, "");
    return normalizado.length > 0 ? normalizado : "plataforma";
  }, [titulo]);

  const archivoInputId = `${baseId}-archivo`;
  const periodoInputId = `${baseId}-periodo`;
  const observacionesInputId = `${baseId}-observaciones`;

  const contenidoFormulario = (
    <>
      <CardTitle tag="h5" className="mb-3">
        {titulo}
      </CardTitle>
      <p className="text-muted mb-4">{descripcion}</p>
      <Form onSubmit={manejarProcesamiento}>
        <div className="mb-3">
          <Label for={archivoInputId}>{archivoLabel}</Label>
              <Input
                id={archivoInputId}
                type="file"
                accept={aceptados}
                innerRef={inputArchivoRef}
                onChange={manejarCambioArchivo}
                disabled={procesando}
              />
              <div className="form-text">{archivoAyuda}</div>
            </div>
            <div className="mb-3">
              <Label for={periodoInputId}>{periodoLabel}</Label>
              <Input
                id={periodoInputId}
                type="text"
                placeholder={periodoPlaceholder}
                value={periodo}
                onChange={(event) => setPeriodo(event.target.value)}
                disabled={procesando}
              />
            </div>
            <div className="mb-4">
              <Label for={observacionesInputId}>{observacionesLabel}</Label>
              <Input
                id={observacionesInputId}
                type="textarea"
                rows={3}
                placeholder={observacionesPlaceholder}
                value={observaciones}
                onChange={(event) => setObservaciones(event.target.value)}
                disabled={procesando}
              />
        </div>
        <div className="d-flex flex-wrap gap-2">
              <Button type="button" color="light" onClick={limpiarFormulario} disabled={!puedeLimpiar || procesando}>
                {limpiarTexto}
              </Button>
              <Button type="submit" color="primary" disabled={!puedeEnviar}>
                {procesando ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Procesando…
                  </>
                ) : (
                  botonTexto
                )}
              </Button>
        </div>
      </Form>
    </>
  );

  const contenido = (
    <div className="mx-auto" style={{ maxWidth: 720 }}>
      {layout === "card" ? (
        <Card className="mb-3">
          <CardBody>{contenidoFormulario}</CardBody>
        </Card>
      ) : (
        <div className="mb-3">{contenidoFormulario}</div>
      )}
      {mensajeError && (
        <Alert color="danger" toggle={() => setMensajeError(null)}>
          {mensajeError}
        </Alert>
      )}
      {mensajeExito && (
        <Alert color="success" toggle={() => setMensajeExito(null)}>
          {mensajeExito}
        </Alert>
      )}
      {detalles.length > 0 && (
        <Card>
          <CardBody>
            <CardTitle tag="h6" className="mb-3">
              {resumenTitulo}
            </CardTitle>
            <ListGroup flush>
              {detalles.map((linea, index) => (
                <ListGroupItem key={`${linea}-${index}`} className="px-0">
                  {linea}
                </ListGroupItem>
              ))}
            </ListGroup>
          </CardBody>
        </Card>
      )}
    </div>
  );

  return contenido;
};

export default ConciliarPlataformaPanel;
